import React from 'react';
import { Controller } from 'react-hook-form';
import * as yup from 'yup';

import Button from '@/core/components/Button';
import HintMessage from '@/core/components/HintMessage';
import { InputToggle } from '@/core/components/inputs/InputToggle';
import Label from '@/core/components/Label';
import useForm from '@/core/hooks/useForm';
import { useEnvContext } from '@/core/lib/env/env.context';
import { ApiMethods } from '@/core/lib/fetch';
import { ApiVersions } from '@/core/lib/fetch/fetch';
import Query from '@/core/lib/new-architecture/query';
import { useTranslationContext } from '@/core/lib/translation/translation.context';
import type { UsersHasControlResult } from '@/core/types/control';

const schema = yup.object({
  newsletter: yup.boolean().required('control.newsletter.required'),
  push: yup.boolean().required('control.push.required'),
});

type Schema = yup.InferType<typeof schema>;

const NotificationControl: React.FC = () => {
  const { t } = useTranslationContext(['common']);
  const { DONNONS_API_URL } = useEnvContext();
  const { invalidate } = Query.user.useInvalidate();

  const { control, onSubmit, errors } = useForm<UsersHasControlResult, Schema>({
    schema,
    apiDefinition: { method: ApiMethods.PUT, url: `${DONNONS_API_URL}${ApiVersions.V1}/user/control/notifications` },
    onSuccess: async () => {
      await invalidate();
    },
  });

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex w-full grow flex-col gap-6">
        <div className="mx-auto flex size-30 items-center justify-center">
          <img loading="lazy" decoding="async" src="/assets/control/notification.svg" alt={t('control.rs.title', { ns: 'common' })} className="object-contain" />
        </div>
        <div className="flex flex-col gap-3 text-center">
          <p className="text-body-primary font-semibold text-content-secondary">{t('control.notification.title', { ns: 'common' })}</p>
          <p className="text-body-secondary font-normal text-content-primary">{t('control.notification.subtitle-1', { ns: 'common' })}</p>
        </div>
        <hr className="border-stroke-secondary" />

        <Controller
          control={control}
          render={({ field: { value, onChange } }) => {
            const { isError, message, isSuccess } = errors.push;
            return (
              <Label htmlFor="push" content={t('control.notification.push-label', { ns: 'common' })} inline>
                <InputToggle id="push" onChange={onChange} checked={value} />
                {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
              </Label>
            );
          }}
          name="push"
          defaultValue={true}
        />
        <Controller
          control={control}
          render={({ field: { value, onChange } }) => {
            const { isError, message, isSuccess } = errors.newsletter;
            return (
              <Label htmlFor="newseletter" content={t('control.notification.newsletter-label', { ns: 'common' })} inline>
                <InputToggle id="newseletter" onChange={onChange} checked={value} />
                {isError && <HintMessage isError={isError} isSuccess={isSuccess} message={message} />}
              </Label>
            );
          }}
          name="newsletter"
          defaultValue={true}
        />
      </div>
      <div className="flex gap-4">
        <Button variant="primary" onClick={onSubmit}>
          {t('control.notification.submit', { ns: 'common' })}
        </Button>
      </div>
    </div>
  );
};

export default NotificationControl;
